import React from 'react';
import type { NodeData, WorkflowNode } from '../nodes/NodeTypes';

interface NodeFieldsProps {
  node: WorkflowNode;
  onChange: (nodeId: string, patch: Partial<NodeData>) => void;
}

const paramLabels: Record<string, string> = {
  filename: '文件路径', seek: '开始时间', duration: '持续时长', width: '宽度', height: '高度', x: '水平位置', y: '垂直位置',
  direction: '旋转方向', speed: '速度倍率', start: '开始时间', fps: '帧率', brightness: '亮度', contrast: '对比度', saturation: '饱和度',
  radius: '模糊半径', power: '模糊强度', amount: '锐化强度', volume: '音量倍率', frequency: '截止频率', threshold: '阈值', ratio: '压缩比',
  attack: '启动时间', release: '释放时间', mode: '混合模式', opacity: '不透明度', codec: '编码器', bitrate: '目标码率', quality: '画质 CRF',
  videoCodec: '视频编码器', audioCodec: '音频编码器', audioBitrate: '音频码率', preset: '编码速度', frameRate: '输出帧率', pixelFormat: '像素格式',
  videoBitrate: '视频码率', fastStart: '快速启动', disableVideo: '禁用视频', disableAudio: '禁用音频', color: '抠像颜色', similarity: '颜色容差', blend: '边缘柔化',
};

function Field({ name, label, value, options, type = 'text', onChange, hint, min, max }: {
  name: string; label: string; value: string; options?: string[]; type?: string; onChange: (value: string) => void; hint?: string; min?: number; max?: number;
}) {
  return (
    <label className="control-field">
      <span className="control-field__label">{label}</span>
      {options?.length ? (
        <select name={name} value={value} onChange={(event) => onChange(event.target.value)}>
          {options.map((option) => <option key={option} value={option}>{option}</option>)}
        </select>
      ) : (
        <input name={name} type={type} min={min} max={max} inputMode={type === 'number' ? 'decimal' : undefined} value={value} onChange={(event) => onChange(event.target.value)} />
      )}
      {hint && <span className="control-field__hint">{hint}</span>}
    </label>
  );
}

function ToggleField({ name, label, checked, onChange }: { name: string; label: string; checked: boolean; onChange: (value: boolean) => void }) {
  return (
    <label className="toggle-field">
      <span>{label}</span>
      <input name={name} type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} />
      <span className="toggle-field__track" aria-hidden="true"><span /></span>
    </label>
  );
}

const NodeFields: React.FC<NodeFieldsProps> = ({ node, onChange }) => {
  const params = node.data.params || {};
  const updateParam = (key: string, value: string) => onChange(node.id, { params: { ...params, [key]: value } });
  if (node.type === 'input') {
    return (
      <div className="fields-stack">
        <Field name={`${node.id}-filename`} label="输入文件路径" value={params.filename || ''} onChange={(value) => updateParam('filename', value)} hint="支持本机路径，例如 D:\\video\\source.mp4" />
        <div className="field-grid">
          <Field name={`${node.id}-seek`} label="开始时间（可选）" value={params.seek || ''} onChange={(value) => updateParam('seek', value)} hint="例如 00:00:12" />
          <Field name={`${node.id}-duration`} label="截取时长（可选）" value={params.duration || ''} onChange={(value) => updateParam('duration', value)} hint="例如 00:00:08" />
        </div>
      </div>
    );
  }

  if (node.type === 'output') {
    return (
      <div className="fields-stack">
        <Field name={`${node.id}-filename`} label="输出文件路径" value={params.filename || ''} onChange={(value) => updateParam('filename', value)} hint="扩展名决定容器，例如 .mp4、.mov、.webm" />
        <div className="field-grid">
          <Field name={`${node.id}-videoCodec`} label="视频编码器" value={params.videoCodec || 'libx264'} options={['libx264', 'libx265', 'av1', 'mpeg4', 'copy']} onChange={(value) => updateParam('videoCodec', value)} />
          <Field name={`${node.id}-quality`} label="画质 CRF" value={params.quality || '23'} type="number" min={0} max={51} onChange={(value) => updateParam('quality', value)} hint="0–51，数值越小画质越高" />
          <Field name={`${node.id}-preset`} label="编码速度" value={params.preset || 'medium'} options={['ultrafast', 'fast', 'medium', 'slow', 'veryslow']} onChange={(value) => updateParam('preset', value)} />
          <Field name={`${node.id}-audioCodec`} label="音频编码器" value={params.audioCodec || 'aac'} options={['aac', 'libmp3lame', 'libopus', 'copy']} onChange={(value) => updateParam('audioCodec', value)} />
          <Field name={`${node.id}-audioBitrate`} label="音频码率" value={params.audioBitrate || '128k'} onChange={(value) => updateParam('audioBitrate', value)} />
          <Field name={`${node.id}-frameRate`} label="输出帧率（可选）" value={params.frameRate || ''} onChange={(value) => updateParam('frameRate', value)} />
          <Field name={`${node.id}-pixelFormat`} label="像素格式（可选）" value={params.pixelFormat || ''} onChange={(value) => updateParam('pixelFormat', value)} />
        </div>
        <div className="toggle-list">
          <ToggleField name={`${node.id}-fastStart`} label="快速启动（适用于 MP4）" checked={params.fastStart === 'true'} onChange={(value) => updateParam('fastStart', String(value))} />
          <ToggleField name={`${node.id}-disableVideo`} label="移除视频流" checked={params.disableVideo === 'true'} onChange={(value) => updateParam('disableVideo', String(value))} />
          <ToggleField name={`${node.id}-disableAudio`} label="移除音频流" checked={params.disableAudio === 'true'} onChange={(value) => updateParam('disableAudio', String(value))} />
        </div>
      </div>
    );
  }

  if (node.data.customFilter) {
    return (
      <div className="fields-stack">
        <label className="control-field">
          <span className="control-field__label">FFmpeg 滤镜表达式</span>
          <textarea name={`${node.id}-custom-filter`} value={node.data.command || ''} onChange={(event) => onChange(node.id, { command: event.target.value })} rows={3} />
        </label>
      </div>
    );
  }

  return (
    <div className="field-grid">
      {Object.entries(params).map(([key, value]) => (
        <Field
          key={key}
          name={`${node.id}-${key}`}
          label={node.data.paramLabels?.[key] || paramLabels[key] || key}
          value={value}
          options={node.data.paramOptions?.[key]}
          onChange={(nextValue) => updateParam(key, nextValue)}
          hint={key === 'speed' ? '1 为原速' : undefined}
        />
      ))}
    </div>
  );
};

export default NodeFields;
